'use client';

import { useIsMobile } from '@/hooks/useIsMobile';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';

interface ProtectedLayoutProviderProps {
  children: ReactNode;
}

type SidebarMode = 'auto-hide' | 'always-expanded';

const ProtectedLayoutContext = createContext<{
  expandedSidebar: boolean;
  setExpandedSidebar: Dispatch<SetStateAction<boolean>>;
  isMobileMatches: boolean;
  sidebarMode: SidebarMode;
  setSidebarMode: Dispatch<SetStateAction<SidebarMode>>;
  toggleSidebarMode: () => void;
  isHovering: boolean;
  setIsHovering: Dispatch<SetStateAction<boolean>>;
  isVisuallyExpanded: boolean;
  isWorkspaceSwitcherOpen: boolean;
  setIsWorkspaceSwitcherOpen: Dispatch<SetStateAction<boolean>>;
  isNotificationsDrawerOpen: boolean;
  setIsNotificationsDrawerOpen: Dispatch<SetStateAction<boolean>>;
}>({
  expandedSidebar: true,
  setExpandedSidebar: () => {},
  isMobileMatches: false,
  sidebarMode: 'auto-hide',
  setSidebarMode: () => {},
  toggleSidebarMode: () => {},
  isHovering: false,
  setIsHovering: () => {},
  isVisuallyExpanded: true,
  isWorkspaceSwitcherOpen: false,
  setIsWorkspaceSwitcherOpen: () => {},
  isNotificationsDrawerOpen: false,
  setIsNotificationsDrawerOpen: () => {},
});

export const ProtectedLayoutProvider: FC<ProtectedLayoutProviderProps> = ({ children }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isWorkspaceSwitcherOpen, setIsWorkspaceSwitcherOpen] = useState(false);
  const [isNotificationsDrawerOpen, setIsNotificationsDrawerOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(() => {
    // Load saved preference from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebar-mode');
      return (saved as SidebarMode) || 'auto-hide';
    }
    return 'auto-hide';
  });
  const isMobileMatches = useIsMobile();

  // Memoize toggleSidebarMode to prevent context recreation on every render
  const toggleSidebarMode = useCallback(() => {
    const newMode = sidebarMode === 'auto-hide' ? 'always-expanded' : 'auto-hide';
    setSidebarMode(newMode);
    // Save preference to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar-mode', newMode);
    }
  }, [sidebarMode]);

  // Derived state: sidebar is expanded if in mobile OR always-expanded mode
  const expandedSidebar = useMemo(() => {
    if (isMobileMatches) return true;
    return sidebarMode === 'always-expanded';
  }, [isMobileMatches, sidebarMode]);

  // Compute visual state: sidebar appears expanded if pinned OR hovering in auto-hide mode
  const isVisuallyExpanded = expandedSidebar || (sidebarMode === 'auto-hide' && isHovering);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      expandedSidebar,
      setExpandedSidebar: () => {
        console.warn('setExpandedSidebar is deprecated and has no effect (state is now derived)');
      },
      isMobileMatches,
      sidebarMode,
      setSidebarMode,
      toggleSidebarMode,
      isHovering,
      setIsHovering,
      isVisuallyExpanded,
      isWorkspaceSwitcherOpen,
      setIsWorkspaceSwitcherOpen,
      isNotificationsDrawerOpen,
      setIsNotificationsDrawerOpen,
    }),
    [
      expandedSidebar,
      isMobileMatches,
      sidebarMode,
      isHovering,
      isVisuallyExpanded,
      isWorkspaceSwitcherOpen,
      isNotificationsDrawerOpen,
      toggleSidebarMode,
    ]
  );

  return (
    <ProtectedLayoutContext.Provider value={contextValue}>
      {children}
    </ProtectedLayoutContext.Provider>
  );
};

export const useProtectedLayoutContext = () => {
  return useContext(ProtectedLayoutContext);
};
